import type { LucideIcon } from "lucide-react";

/**
 * Typed detail-header model.
 *
 * Each registered entity builds a `DetailHeaderModel` from a `Pick<>` of its
 * codegen GraphQL type (`@/src/gql/graphql`), so every field the header reads
 * is schema-checked at compile time (the SLACK_FORMATTERS_USE_CODEGEN_TYPES
 * pattern applied to the detail header). The fields each builder consumes are
 * declared in a `*_HEADER_FIELDS` const and guarded against the entity's
 * detail query by `tests/utils/detail-header-model-coverage.test.ts`.
 */

/**
 * Names the single editable DB column a header element represents, so the
 * header can edit it in place AND the inline edit panel can drop it (no
 * duplicate editor). Set ONLY on elements that map 1:1 to one editable scalar
 * column — never on derived/computed values (e.g. a "followed up this week"
 * fact) or relation links. Builders type it as `keyof T` via a per-entity
 * `*_HEADER_EDITABLE_FIELDS satisfies readonly (keyof T)[]` union.
 */
export type HeaderEditableFieldId = string;

/** One piece of the subtitle line; segments are joined with a bullet. */
export interface HeaderSubtitleSegment {
  text: string;
  /** Internal route — rendered as a link when present. */
  href?: string;
  /** When set, this segment is the editable column `fieldId` (see above). */
  fieldId?: HeaderEditableFieldId;
}

/**
 * Link/resolution behavior for a header fact.
 *
 * `"user"` carries an `app_users.id` UUID in `value`; the view resolves it to a
 * friendly name (never render a raw UUID in the header).
 */
export type HeaderFactKind = "text" | "email" | "phone" | "internal" | "external" | "user";

/**
 * A small pill rendered after a fact's value, marking it as something other
 * than the entity's own data — e.g. a contact method INHERITED from the
 * company's shared line ("Company line"). A badged fact must never carry a
 * `fieldId`: it is not this row's column, so it is not editable here.
 */
export interface HeaderFactBadge {
  text: string;
  /** Hover explanation — where the value actually lives. */
  title?: string;
}

/** Compact icon+value item rendered in the facts row under the subtitle. */
export interface HeaderFact {
  id: string;
  icon: LucideIcon;
  value: string;
  /** Marks the value as not the entity's own (see HeaderFactBadge). */
  badge?: HeaderFactBadge;
  /**
   * Replaces the surrounding entity context when this fact's value is opened in
   * a compose / send-text modal. Set ONLY for a method that belongs to another
   * record: activity on a company's shared line must be logged against that
   * company's line anchor, never against the person whose card it was launched
   * from. Structural on purpose — the header model stays free of CRM imports.
   */
  methodContext?: { item: Record<string, unknown>; entityType: string };
  /** Optional short label rendered before the value (e.g. "Assigned"). */
  label?: string;
  kind?: HeaderFactKind;
  /** Required for kind "internal" | "external". */
  href?: string;
  /** When set, this fact is the editable column `fieldId` (see HeaderEditableFieldId). */
  fieldId?: HeaderEditableFieldId;
  /**
   * Inline "add" prompt shown when `value` is empty AND this fact is editable
   * (`fieldId` set) AND editing is enabled — e.g. "Add phone". An empty fact
   * with no `fieldId`, or for a read-only viewer, is dropped instead of shown.
   * This is the missing-editable-data inline-add pattern: a gap the user can
   * fill in place, never a described dead end.
   */
  placeholder?: string;
}

/** Small KPI stat rendered in the metrics strip (sales / engineer numbers). */
export interface HeaderMetric {
  id: string;
  label: string;
  value: string;
  /** Secondary detail rendered after the value, de-emphasized. */
  hint?: string;
  /** When set, this metric is the editable column `fieldId` (see HeaderEditableFieldId). */
  fieldId?: HeaderEditableFieldId;
}

export interface DetailHeaderModel {
  /** Replaces the description line when non-empty; falls back otherwise. */
  subtitleSegments: HeaderSubtitleSegment[];
  facts: HeaderFact[];
  metrics: HeaderMetric[];
}

/** Render an ISO date(-time) string as a locale date; null when unusable. */
export function formatHeaderDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString();
}
